import {
  ETIQUETA_INSTRUMENTO,
  ETIQUETA_INSTRUMENTO_EN,
  INSTRUMENTOS_DE_CREDITO,
  type Financiador,
  type InstrumentoDeCredito,
  type ResultadoDeEliminarFinanciador,
} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  useCrearFinanciador,
  useEliminarFinanciador,
  useFinanciadores,
  useGuardarFinanciador,
} from '../api/financiacion';
import {AYUDA_FINANCIADORES as A} from '../componentes/ayuda-exportaciones';
import {AYUDA_TERMINOS_DE_FINANCIADOR as T} from '../componentes/ayuda-financiacion-bluequest';
import {DocumentosDelFinanciador} from '../componentes/financiador-documentos';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PorReasignarABanco} from '../componentes/por-reasignar-a-banco';
import {usePermisos} from '../componentes/sesion-contexto';
import {et, tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

const campo = 'mt-1 block w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta';
const aCupo = (t: string) => (t.trim() === '' ? null : Number(t.replace(/[^\d]/g, '')));

// Configuracion de campos → Financiadores y cupos (decision 0021). Los
// financiadores son los bancos (con sus tipos de credito y un cupo) o
// Bluequest, cuyo cupo es la suma de sus bancos (Daniel, 15/10/2026). Cada
// cambio queda en la bitacora.
export function PaginaFinanciadores() {
  const consulta = useFinanciadores();
  const permisos = usePermisos();
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const lista = consulta.data ?? [];
  const visibles = lista.filter(f => f.activo || mostrarInactivos);
  return (
    <section className="max-w-5xl space-y-4">
      <Link to="/configuracion-de-campos" className="text-sm text-verde hover:underline">
        {tr('← Configuración de campos', '← Field settings')}
      </Link>
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">
          {tr('Financiadores y cupos', 'Financers and credit lines')}
        </h1>
        <p className="text-sm text-apagado">
          {tr(
            'Los financiadores son los bancos, cada uno con sus tipos de crédito (carta de crédito, buyer\'s credit, cash credit) y un solo cupo, o Bluequest, cuyo cupo es la suma de sus bancos. Un cupo vacío queda "por definir". "Usa el cupo de" junta dos líneas en un solo cupo (BLQM, lo de Mineros, usa el de BLQ). Los cambios se ven en Seguimiento → Financiaciones y en Cashflow.',
            'Financers are the banks, each with its credit types (letter of credit, buyer\'s credit, cash credit) and a single line, or Bluequest, whose line is the sum of its banks. An empty line stays "to be defined". "Uses the line of" puts two lines under one limit (BLQM, Mineros, uses BLQ). Changes show in Tracking → Financing and in Cashflow.',
          )}
        </p>
      </div>
      {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
      {permisos.editar && <NuevoFinanciador />}
      {permisos.editar && <PorReasignarABanco financiadores={lista} />}
      {lista.some(f => !f.activo) && (
        <label className="flex items-center gap-2 text-xs text-apagado">
          <input type="checkbox" checked={mostrarInactivos} onChange={e => setMostrarInactivos(e.target.checked)} />
          {tr('Mostrar los inactivos', 'Show inactive ones')} ({lista.filter(f => !f.activo).length})
        </label>
      )}
      <div className="space-y-3">
        {visibles.map(f => (
          <div key={f.codigo}>
            <Fila
              f={f}
              puedeEditar={permisos.editar}
              // Un cupo se comparte con otro financiador que financia y tiene cupo propio.
              duenos={lista.filter(x => x.codigo !== f.codigo && x.financia && !x.comparteCupoCon).map(x => x.codigo)}
              // Quienes usan este cupo: el total es la suma (BLQ + BLQM, Daniel 13/10/2026).
              usan={lista.filter(x => x.comparteCupoCon === f.codigo)}
            />
            <DocumentosDelFinanciador f={f} puedeEditar={permisos.editar} />
            {permisos.administrar && (
              <EliminarFinanciador f={f} otros={lista.filter(x => x.codigo !== f.codigo && x.activo)} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function TiposDeCredito({
  valor,
  onCambiar,
  deshabilitado,
}: {
  valor: InstrumentoDeCredito[];
  onCambiar: (v: InstrumentoDeCredito[]) => void;
  deshabilitado?: boolean;
}) {
  const etiqueta = et(ETIQUETA_INSTRUMENTO, ETIQUETA_INSTRUMENTO_EN);
  return (
    <fieldset className="text-xs text-apagado" disabled={deshabilitado}>
      <legend>
        {tr('Tipos de crédito', 'Credit types')}
        <NotaDeAyuda texto={T.tiposDeCredito} />
      </legend>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-tinta">
        {INSTRUMENTOS_DE_CREDITO.map(i => (
          <label key={i} className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={valor.includes(i)}
              onChange={e => onCambiar(e.target.checked ? [...valor, i] : valor.filter(x => x !== i))}
            />
            {etiqueta[i]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// Financiador nuevo: un banco con su codigo, sus tipos de credito y su cupo.
function NuevoFinanciador() {
  const crear = useCrearFinanciador();
  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [cupo, setCupo] = useState('');
  const [tipos, setTipos] = useState<InstrumentoDeCredito[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  if (!abierto)
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro"
      >
        {tr('＋ Nuevo financiador', '＋ New financer')}
      </button>
    );
  return (
    <form
      className="grid gap-3 rounded-lg border border-verde/40 bg-white p-4 sm:grid-cols-[7rem_1fr_12rem] sm:items-end"
      aria-label={tr('Nuevo financiador', 'New financer')}
      onSubmit={async e => {
        e.preventDefault();
        setAviso(null);
        try {
          await crear.mutateAsync({codigo, nombre, cupoUsd: aCupo(cupo), instrumentos: tipos});
          setAbierto(false);
          setCodigo('');
          setNombre('');
          setCupo('');
          setTipos([]);
        } catch (error) {
          setAviso(error instanceof Error ? error.message : String(error));
        }
      }}
    >
      <label className="block text-xs text-apagado">
        {tr('Código', 'Code')}
        <NotaDeAyuda texto={T.codigoNuevo} dentroDeEtiqueta />
        <input
          value={codigo}
          onChange={e => setCodigo(e.target.value.toUpperCase())}
          placeholder="ICICI"
          className={campo}
        />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Nombre', 'Name')}
        <NotaDeAyuda texto={A.nombre} dentroDeEtiqueta />
        <input value={nombre} onChange={e => setNombre(e.target.value)} className={campo} />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Cupo (USD)', 'Credit line (USD)')}
        <NotaDeAyuda texto={A.cupo} dentroDeEtiqueta />
        <input
          value={cupo}
          inputMode="numeric"
          onChange={e => setCupo(e.target.value)}
          className={`${campo} text-right tabular-nums`}
        />
      </label>
      <div className="sm:col-span-2">
        <TiposDeCredito valor={tipos} onCambiar={setTipos} />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!codigo.trim() || !nombre.trim() || crear.isPending}
          className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {tr('Crear', 'Create')}
        </button>
        <button
          type="button"
          onClick={() => setAbierto(false)}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm"
        >
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
      {aviso && <p className="text-xs text-alerta sm:col-span-3">{aviso}</p>}
    </form>
  );
}

function Fila({
  f,
  puedeEditar,
  duenos,
  usan,
}: {
  f: Financiador;
  puedeEditar: boolean;
  duenos: string[];
  usan: Financiador[];
}) {
  const guardar = useGuardarFinanciador();
  const [nombre, setNombre] = useState(f.nombre);
  const [cupo, setCupo] = useState(f.cupoPropioUsd === null ? '' : String(f.cupoPropioUsd));
  const [comparte, setComparte] = useState(f.comparteCupoCon ?? '');
  const [notas, setNotas] = useState(f.notas ?? '');
  const [tipos, setTipos] = useState<InstrumentoDeCredito[]>(f.instrumentos);
  const [activo, setActivo] = useState(f.activo);
  const [aviso, setAviso] = useState<string | null>(null);
  const cupoNumero = aCupo(cupo);
  const valido = nombre.trim() !== '' && (cupoNumero === null || (Number.isFinite(cupoNumero) && cupoNumero >= 0));
  const cambio =
    nombre !== f.nombre ||
    cupoNumero !== f.cupoPropioUsd ||
    (comparte || null) !== f.comparteCupoCon ||
    (notas || null) !== f.notas ||
    activo !== f.activo ||
    [...tipos].sort().join() !== [...f.instrumentos].sort().join();
  const enviar = async () => {
    setAviso(null);
    try {
      await guardar.mutateAsync({
        codigo: f.codigo,
        cambios: {
          nombre: nombre.trim(),
          cupoUsd: cupoNumero,
          comparteCupoCon: comparte || null,
          notas: notas.trim() || null,
          instrumentos: tipos,
          activo,
        },
      });
      setAviso(tr('Guardado.', 'Saved.'));
    } catch (e) {
      setAviso(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <form
      className={`grid gap-3 rounded-lg border border-linea bg-white p-4 sm:grid-cols-[6rem_1fr_12rem_10rem_1fr_auto] sm:items-end ${activo ? '' : 'opacity-70'}`}
      aria-label={tr(`Financiador ${f.codigo}`, `Financer ${f.codigo}`)}
      onSubmit={e => {
        e.preventDefault();
        void enviar();
      }}
    >
      <div>
        <div className="text-xs text-apagado">
          {tr('Código', 'Code')}
          <NotaDeAyuda texto={A.codigo} />
        </div>
        <div className="font-medium">{f.codigo}</div>
        {!f.financia && <div className="text-xs text-apagado">{tr('venta directa', 'direct sale')}</div>}
        {!f.activo && <div className="text-xs text-apagado">{tr('inactivo', 'inactive')}</div>}
      </div>
      <label className="block text-xs text-apagado">
        {tr('Nombre', 'Name')}
        <NotaDeAyuda texto={A.nombre} dentroDeEtiqueta />
        <input value={nombre} disabled={!puedeEditar} onChange={e => setNombre(e.target.value)} className={campo} />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Cupo (USD)', 'Credit line (USD)')}
        <NotaDeAyuda texto={A.cupo} dentroDeEtiqueta />
        <input
          value={f.cupoPorBancos ? String(f.cupoUsd ?? '') : cupo}
          disabled={!puedeEditar || !f.financia || f.cupoPorBancos}
          inputMode="numeric"
          placeholder={f.financia ? tr('por definir', 'to be defined') : tr('no aplica', 'not applicable')}
          onChange={e => setCupo(e.target.value)}
          className={`${campo} text-right tabular-nums`}
        />
        {f.cupoPorBancos ? (
          <span className="text-[11px]" data-prueba="cupo-de-bancos">
            {tr(`suma de sus bancos: USD ${numero(f.cupoUsd ?? 0)}`, `sum of its banks: USD ${numero(f.cupoUsd ?? 0)}`)}
          </span>
        ) : comparte ? (
          <span className="text-[11px]">{tr(`se suma al cupo de ${comparte}`, `added to the ${comparte} line`)}</span>
        ) : usan.length > 0 ? (
          <span className="text-[11px]" data-prueba="cupo-total">
            {tr(
              `Total con ${usan.map(x => x.codigo).join(', ')}: USD ${numero((cupoNumero ?? 0) + usan.reduce((s, x) => s + (x.cupoUsd ?? 0), 0))}`,
              `Total with ${usan.map(x => x.codigo).join(', ')}: USD ${numero((cupoNumero ?? 0) + usan.reduce((s, x) => s + (x.cupoUsd ?? 0), 0))}`,
            )}
          </span>
        ) : (
          cupoNumero !== null &&
          Number.isFinite(cupoNumero) && <span className="text-[11px]">USD {numero(cupoNumero)}</span>
        )}
      </label>
      <label className="block text-xs text-apagado">
        {tr('Usa el cupo de', 'Uses the line of')}
        <NotaDeAyuda texto={A.comparte} dentroDeEtiqueta />
        <select
          value={comparte}
          disabled={!puedeEditar || !f.financia}
          onChange={e => setComparte(e.target.value)}
          className={campo}
        >
          <option value="">{tr('Cupo propio', 'Own line')}</option>
          {duenos.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-apagado">
        {tr('Notas', 'Notes')}
        <NotaDeAyuda texto={A.notas} dentroDeEtiqueta />
        <input value={notas} disabled={!puedeEditar} onChange={e => setNotas(e.target.value)} className={campo} />
      </label>
      {puedeEditar && (
        <div className="flex flex-col items-start gap-1">
          <button
            type="submit"
            disabled={!valido || !cambio || guardar.isPending}
            className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {tr('Guardar', 'Save')}
          </button>
          {aviso && <span className="text-xs">{aviso}</span>}
        </div>
      )}
      {f.financia && (
        <div className="flex flex-wrap items-end gap-6 sm:col-span-6">
          <TiposDeCredito valor={tipos} onCambiar={setTipos} deshabilitado={!puedeEditar} />
          <label className="flex items-center gap-1 text-xs text-apagado">
            <input
              type="checkbox"
              checked={activo}
              disabled={!puedeEditar}
              onChange={e => setActivo(e.target.checked)}
            />
            {tr('Activo', 'Active')}
            <NotaDeAyuda texto={T.financiadorActivo} dentroDeEtiqueta />
          </label>
        </div>
      )}
    </form>
  );
}

// Eliminar un financiador (Daniel, 16/10/2026), solo administradores: primero
// dice que tiene y a quien pasan sus exportaciones; despues pide confirmar.
function EliminarFinanciador({f, otros}: {f: Financiador; otros: Financiador[]}) {
  const eliminar = useEliminarFinanciador();
  const [abierto, setAbierto] = useState(false);
  const [destino, setDestino] = useState('');
  const [revision, setRevision] = useState<ResultadoDeEliminarFinanciador | null>(null);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const revisar = async (a: string) => {
    setMensaje(null);
    try {
      setRevision(await eliminar.mutateAsync({codigo: f.codigo, peticion: {modo: 'revisar', reasignarA: a || null}}));
    } catch (e) {
      setMensaje({
        texto: e instanceof ErrorDeApi ? e.message : tr('No se pudo revisar.', 'Could not review.'),
        error: true,
      });
    }
  };
  const confirmar = async () => {
    if (!revision) return;
    const aQuien = destino
      ? tr(
          `Sus ${revision.exportaciones.length} exportaciones pasan a ${destino}.`,
          `Its ${revision.exportaciones.length} exports move to ${destino}.`,
        )
      : tr(
          `Sus ${revision.exportaciones.length} exportaciones quedan sin financiador.`,
          `Its ${revision.exportaciones.length} exports are left without financer.`,
        );
    if (
      !window.confirm(
        tr(
          `¿Seguro que quieres eliminar el financiador ${f.codigo} (${f.nombre})? ${aQuien} Queda en la bitácora y se puede deshacer desde ahí.`,
          `Are you sure you want to delete the financer ${f.codigo} (${f.nombre})? ${aQuien} It stays in the audit log and can be undone from there.`,
        ),
      )
    )
      return;
    try {
      const r = await eliminar.mutateAsync({
        codigo: f.codigo,
        peticion: {modo: 'eliminar', reasignarA: destino || null},
      });
      setMensaje({
        texto: tr(`Listo: se eliminó ${r.financiador}.`, `Done: ${r.financiador} was deleted.`),
        error: false,
      });
    } catch (e) {
      setMensaje({
        texto: e instanceof ErrorDeApi ? e.message : tr('No se pudo eliminar.', 'Could not delete.'),
        error: true,
      });
    }
  };
  if (!abierto)
    return (
      <button
        type="button"
        onClick={() => {
          setAbierto(true);
          void revisar(destino);
        }}
        className="mt-1 text-xs text-alerta hover:underline"
      >
        🗑 {tr(`Eliminar el financiador ${f.codigo}`, `Delete financer ${f.codigo}`)}
      </button>
    );
  return (
    <div
      className="mt-1 space-y-2 rounded-lg border border-alerta/40 bg-white p-3 text-sm"
      role="region"
      aria-label={tr(`Eliminar ${f.codigo}`, `Delete ${f.codigo}`)}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-alerta">
          {tr(`Eliminar el financiador ${f.codigo}`, `Delete financer ${f.codigo}`)}
          <NotaDeAyuda texto={A.eliminar} />
        </span>
        <button
          type="button"
          onClick={() => setAbierto(false)}
          aria-label={tr('Cerrar', 'Close')}
          className="text-apagado"
        >
          ×
        </button>
      </div>
      <label className="block max-w-sm text-xs text-apagado">
        {tr('Pasar sus exportaciones a', 'Move its exports to')}
        <select
          value={destino}
          onChange={e => {
            setDestino(e.target.value);
            void revisar(e.target.value);
          }}
          className={campo}
        >
          <option value="">{tr('— Dejarlas sin financiador —', '— Leave them without financer —')}</option>
          {otros.map(x => (
            <option key={x.codigo} value={x.codigo}>
              {x.codigo} · {x.nombre}
            </option>
          ))}
        </select>
      </label>
      {revision && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs" data-prueba="revision-eliminar-financiador">
          {revision.lineas.map(l => (
            <li key={l}>{l}</li>
          ))}
          {revision.exportaciones.length > 0 && (
            <li className="text-apagado">
              {revision.exportaciones.slice(0, 20).join(', ')}
              {revision.exportaciones.length > 20 ? ' …' : ''}
            </li>
          )}
        </ul>
      )}
      {mensaje && (
        <p
          role={mensaje.error ? 'alert' : 'status'}
          className={`text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}
        >
          {mensaje.texto}
        </p>
      )}
      <button
        type="button"
        onClick={() => void confirmar()}
        disabled={!revision || eliminar.isPending}
        className="rounded-md bg-alerta px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
      >
        {tr(`Eliminar ${f.codigo}`, `Delete ${f.codigo}`)}
      </button>
    </div>
  );
}
